import { useEffect, useRef } from 'react';
import * as THREE from 'three';

/**
 * LifeBoard3D - Main visual canvas for life simulation
 * 
 * Renders isometric life board with:
 * - 4 zones (Home, Auto, Health, Liability)
 * - Ambient motion
 * - Event visualization
 * - Soft glows and particles
 * 
 * Aesthetic: A24 style - calm, dark, intentional motion
 */
export default function LifeBoard3D({ life, queuedEvents, isPaused }) {
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const zoneObjectsRef = useRef({});
  const particleSystemsRef = useRef([]);

  // Initialize Three.js scene
  useEffect(() => {
    if (!canvasRef.current) return;

    const width = canvasRef.current.clientWidth;
    const height = canvasRef.current.clientHeight;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a0f); // Dark charcoal
    scene.fog = new THREE.Fog(0x0a0a0f, 20, 50);
    sceneRef.current = scene;

    // Camera - Isometric view
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(15, 15, 15);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: false 
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    canvasRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
    scene.add(ambientLight);

    // Directional light (main)
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.7);
    directionalLight.position.set(10, 20, 10);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    scene.add(directionalLight);

    // Ground plane
    const groundGeometry = new THREE.PlaneGeometry(40, 40);
    const groundMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x1a1a1f,
      roughness: 0.8,
      metalness: 0.2
    });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.5;
    ground.receiveShadow = true;
    scene.add(ground);

    // Create 4 zone structures
    createZoneStructures(scene, zoneObjectsRef);

    // Create particle systems
    createParticleSystems(scene, particleSystemsRef);

    // Animation loop
    let animationFrameId;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      
      if (!isPaused) {
        // Rotate camera slowly
        camera.position.x = Math.cos(Date.now() * 0.0001) * 15;
        camera.position.z = Math.sin(Date.now() * 0.0001) * 15;
        camera.lookAt(0, 0, 0);

        // Update particles
        particleSystemsRef.current.forEach(system => {
          system.rotation.y += 0.001;
        });
      }
      
      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      if (!canvasRef.current) return;
      const width = canvasRef.current.clientWidth;
      const height = canvasRef.current.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
      renderer.dispose();
      if (canvasRef.current && renderer.domElement) {
        canvasRef.current.removeChild(renderer.domElement);
      }
    };
  }, [isPaused]);

  // Update zone visuals based on life state
  useEffect(() => {
    if (!life || !zoneObjectsRef.current) return;

    // Update zone colors and heights based on condition
    Object.keys(life.zones).forEach(zoneKey => {
      const zoneObj = zoneObjectsRef.current[zoneKey];
      if (!zoneObj) return;

      const zone = life.zones[zoneKey];
      const condition = zone.condition / 100;

      // Color based on condition
      const color = condition > 0.7 ? 0x4ade80 : // Green
                    condition > 0.4 ? 0xfbbf24 : // Yellow
                    0xef4444; // Red

      if (zoneObj.material) {
        zoneObj.material.color.set(color);
        zoneObj.material.emissive.set(color);
        zoneObj.material.emissiveIntensity = 0.2 + (1 - condition) * 0.3;
      }

      // Scale based on condition
      zoneObj.scale.y = 0.5 + condition * 0.5;
    });
  }, [life]);

  // Visualize queued events
  useEffect(() => {
    if (!sceneRef.current || !queuedEvents || !zoneObjectsRef.current) return;

    // Clean up previous event indicators (iterate backwards to safely remove)
    const indicatorsToRemove = sceneRef.current.children.filter(child => child.userData.isEventIndicator);
    indicatorsToRemove.forEach(indicator => {
      sceneRef.current.remove(indicator);
      if (indicator.geometry) indicator.geometry.dispose();
      if (indicator.material) indicator.material.dispose();
    });

    // Add warning indicators for queued events
    queuedEvents.forEach(event => {
      const zoneObj = zoneObjectsRef.current[event.zone];
      if (!zoneObj) return;

      // Create pulsing ring around targeted zone
      const ringGeometry = new THREE.RingGeometry(3, 3.5, 32);
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: event.isShock ? 0xff0000 : event.category === 'systemic' ? 0xffa500 : 0xffff00,
        transparent: true,
        opacity: 0.3 + event.telegraphProgress * 0.5,
        side: THREE.DoubleSide
      });
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.position.copy(zoneObj.position);
      ring.position.y = 0.1; // Just above ground
      ring.rotation.x = -Math.PI / 2;
      ring.userData.isEventIndicator = true;
      
      sceneRef.current.add(ring);

      // Add particle burst for shock events
      if (event.isShock && event.telegraphProgress > 0.7) {
        const burstGeometry = new THREE.BufferGeometry();
        const burstPositions = new Float32Array(50 * 3);
        
        for (let i = 0; i < 50; i++) {
          const angle = (i / 50) * Math.PI * 2;
          const radius = 2 + Math.random() * 2;
          burstPositions[i * 3] = zoneObj.position.x + Math.cos(angle) * radius;
          burstPositions[i * 3 + 1] = 0.5 + Math.random() * 2;
          burstPositions[i * 3 + 2] = zoneObj.position.z + Math.sin(angle) * radius;
        }
        
        burstGeometry.setAttribute('position', new THREE.BufferAttribute(burstPositions, 3));
        const burstMaterial = new THREE.PointsMaterial({
          color: 0xff0000,
          size: 0.2,
          transparent: true,
          opacity: event.telegraphProgress
        });
        
        const burst = new THREE.Points(burstGeometry, burstMaterial);
        burst.userData.isEventIndicator = true;
        sceneRef.current.add(burst);
      }
    });
  }, [queuedEvents]);

  return (
    <div
      ref={canvasRef}
      className="w-full h-[500px] rounded-xl overflow-hidden shadow-2xl border border-white/10"
    />
  );
}

// Create 4 zone structures
function createZoneStructures(scene, zoneObjectsRef) {
  const zones = [
    { key: 'home', position: [-5, 0, -5], color: 0xffe66d, icon: '🏠' },
    { key: 'auto', position: [5, 0, -5], color: 0xff6b6b, icon: '🚗' },
    { key: 'health', position: [-5, 0, 5], color: 0x4ecdc4, icon: '💚' },
    { key: 'liability', position: [5, 0, 5], color: 0xa8dadc, icon: '⚖️' }
  ];

  zones.forEach(({ key, position, color }) => {
    const geometry = new THREE.BoxGeometry(4, 3, 4);
    const material = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.2,
      roughness: 0.5,
      metalness: 0.3
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(...position);
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    scene.add(mesh);
    zoneObjectsRef.current[key] = mesh;

    // Add glow effect
    const glowGeometry = new THREE.SphereGeometry(3, 32, 32);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.1
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    glow.position.set(...position);
    scene.add(glow);
  });
}

// Create ambient particle systems
function createParticleSystems(scene, particleSystemsRef) {
  const particleCount = 200;
  const positions = new Float32Array(particleCount * 3);

  for (let i = 0; i < particleCount; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 30;
    positions[i * 3 + 1] = Math.random() * 10;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 30;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const material = new THREE.PointsMaterial({
    color: 0xffffff,
    size: 0.05,
    transparent: true,
    opacity: 0.3
  });

  const particles = new THREE.Points(geometry, material);
  scene.add(particles);
  particleSystemsRef.current.push(particles);
}

